import { Td, Tr, Text } from '@chakra-ui/react'
import { Field } from '@micin-jp/chicken-schema'

import { checkVisibleIf } from './checkVisibleIf'
import { FieldValue, FileFieldValue } from '../../types'
import { FlattenFieldMap } from '../../utils/getFlattenFields'
import { getFieldValueText } from '../../utils/getValueText'

type Props = {
  field: Field
  fieldValues: FieldValue[]
  fieldMap: FlattenFieldMap
  index: number
}

/**
 * 指定されたフィールドの値を取得
 */
const findFieldValue = (
  fieldValues: FieldValue[],
  fid: string,
  index: number,
): FieldValue | undefined => {
  return fieldValues.find(fv => fv.fid === fid && fv.index === index)
}

/**
 * ファイルフィールドのファイル名一覧を取得
 */
const getFileNames = (fieldValue: FileFieldValue): string => {
  if (!fieldValue.files || fieldValue.files.length === 0) {
    return ''
  }
  return fieldValue.files
    .sort((a, b) => a.order - b.order)
    .map(file => file.uploadedFile.name)
    .join(', ')
}

export const PrintField: React.FC<Props> = ({
  field,
  fieldValues,
  fieldMap,
  index,
}) => {
  // visibleIf条件をチェック
  if (!checkVisibleIf(field, fieldValues, fieldMap, index)) {
    return null
  }

  const fieldValue = findFieldValue(fieldValues, field.fid, index)
  const fieldType = field.typeDef.type

  // Section, RepeatableSectionはこのコンポーネントでは処理しない
  if (fieldType === 'Section' || fieldType === 'RepeatableSection') {
    return null
  }

  // Labelフィールドは1列で表示
  if (fieldType === 'Label') {
    return (
      <Tr className="print-label-row">
        <Td
          colSpan={2}
          py="3"
          px="4"
          borderBottom="1px solid black"
          color="gray.600"
        >
          <Text whiteSpace="pre-wrap">{field.name}</Text>
        </Td>
      </Tr>
    )
  }

  // ファイルフィールドの特別処理
  if (fieldType === 'File') {
    const fileValue = fieldValue as FileFieldValue | undefined
    const displayValue = fileValue ? getFileNames(fileValue) : ''

    return (
      <Tr className="print-field-row">
        <Td
          w="240px"
          py="3"
          px="4"
          borderBottom="1px solid black"
          borderRight="1px solid black"
          verticalAlign="top"
        >
          <Text fontWeight="medium">{field.name}</Text>
        </Td>
        <Td
          py="3"
          px="4"
          borderBottom="1px solid black"
          verticalAlign="top"
          color="gray.600"
        >
          <Text whiteSpace="pre-wrap">{displayValue}</Text>
        </Td>
      </Tr>
    )
  }

  // その他のフィールドは左列にフィールド名、右列に値
  const displayValue = fieldValue ? getFieldValueText(fieldValue, fieldMap) : ''

  return (
    <Tr className="print-field-row">
      <Td
        w="240px"
        py="3"
        px="4"
        borderBottom="1px solid black"
        borderRight="1px solid black"
        verticalAlign="top"
      >
        <Text fontWeight="medium">{field.name}</Text>
      </Td>
      <Td
        py="3"
        px="4"
        borderBottom="1px solid black"
        verticalAlign="top"
        color="gray.600"
      >
        <Text whiteSpace="pre-wrap">{displayValue}</Text>
      </Td>
    </Tr>
  )
}
